/**
 * What the text typed into the "Copy others" box on the conflict check request resolves to.
 *
 * WHY THIS EXISTS. The box used to act only on a click in the list or on Enter. Anything else,
 * such as typing a colleague's name and going straight to Submit, left the name sitting in the
 * box, and the request went out without that person and without a word. That is how the first
 * report of it came in: a requester typed Renee Chandler, who is in the index, never picked the
 * name from the list, and the request was sent without them.
 *
 * So the rules, in one place and tested:
 *   - A typed full address is taken as written, subject to the usual checks.
 *   - A typed name becomes a person only when exactly one person is meant: an exact name match,
 *     or a search that found just one. Anything wider is a question for the requester, not a
 *     guess, because copying the wrong person sends them the parties being checked.
 *   - A name is never resolved against results fetched for different text. The list lags the
 *     typing by a moment, and resolving "Renee S" against the results for "Renee" would pick
 *     whoever those were.
 *
 * Pure, so none of it needs a rendered component to test.
 */

export const FIRM_DOMAIN = 'aalrr.com';
export const MAX_CC = 20;

export const normalise = (value) => String(value || '').trim().toLowerCase();

export const isFirmAddress = (email) =>
  /^[^\s@]+@[^\s@.]+\.[^\s@]+$/.test(email) && email.endsWith(`@${FIRM_DOMAIN}`);

/** The checks every address goes through, however it was arrived at. */
function checkAddress(email, { selected, self }) {
  if (!isFirmAddress(email)) return { problem: `Only @${FIRM_DOMAIN} addresses can be copied.` };
  if (email === normalise(self)) return { problem: 'You are already copied on your own request.' };
  if (selected.includes(email)) return { problem: 'Already on the list.' };
  if (selected.length >= MAX_CC) return { problem: `That is the most this can copy (${MAX_CC}).` };
  return { add: email };
}

/**
 * @param {object} args
 * @param {string} args.text          what is in the box
 * @param {Array<{name?: string, email?: string}>|null} args.people
 *        the search results for exactly this text, or null when none have arrived for it yet
 * @param {string[]} [args.selected]  addresses already on the list
 * @param {string} [args.self]        the requester's own address
 * @returns {{nothing: true} | {lookup: true} | {add: string} | {problem: string}}
 */
export function resolveTyped({ text, people, selected = [], self = '' }) {
  const typed = String(text || '').trim();
  const clean = normalise(typed);
  if (!clean) return { nothing: true };

  // Anything with an @ is an address, taken as written.
  if (clean.includes('@')) return checkAddress(clean, { selected, self });

  if (!Array.isArray(people)) return { lookup: true };

  const found = people.filter((person) => isFirmAddress(normalise(person && person.email)));
  if (!found.length) return { problem: `No one at the firm matches “${typed}”.` };

  const exact = found.filter((person) => normalise(person.name) === clean);
  const pick = exact.length === 1 ? exact[0] : found.length === 1 ? found[0] : null;
  if (!pick) return { problem: `More than one person matches “${typed}”. Pick one from the list.` };

  return checkAddress(normalise(pick.email), { selected, self });
}
